import { memo, useCallback, useEffect, useRef } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Ellipsis, Hourglass, RotateCcw, TriangleAlert } from 'lucide-react';

import { useStableHandler } from '@/shared/lib/useStableHandler';
import { RelationIcon } from '@/shared/ui';

import { accountStateClass, accountStateKey } from '../model/accountState';
import { NEAR_SUMS_DEPTH } from '../model/sums';
import type { SumsScope } from '../model/sums';
import { isExpandable } from '../model/tree';
import type { ElementRow, TreeRow } from '../model/tree';
import type {
  ElementWorkCounts,
  ElementWorkRollup,
  OwnerView,
  RelationDirection,
} from '../model/types';
import styles from './Constellation.module.css';

/**
 * Drzewko jako **płaska lista wierszy** ze wzorcem `tree` z WAI-ARIA.
 *
 * Płaska, bo wzorzec wymaga jednego pojemnika `role="tree"` i rodzeństwa
 * `role="treeitem"` z jawnym `aria-level` — a nawigacja klawiaturą potrzebuje
 * kolejności wierszy jako jednego wymiaru. Zagnieżdżone listy dałyby ten sam
 * obraz i inną semantykę.
 *
 * ⭐ Kontrakt kliknięcia (decyzja 157, werdykt usera 2026-08-26 do makiety B0):
 * **klik w wiersz = zaznaczenie + szczegóły** w panelu-inspektorze obok · trójkąt = rozwijanie ·
 * **dwuklik = wejście** (element staje się nowym korzeniem) · klik w liczby pracy = zaznaczenie
 * z panelem przewiniętym do zobowiązań. Klawiatura (decyzja 158): strzałki tylko przenoszą
 * ognisko, Enter zaznacza, Shift+Enter wchodzi, Escape czyści.
 *
 * ⚠ Do 2026-08-26 nazwa była linkiem do `/constellation/{id}` (spec sek. 4 w pierwotnym
 * brzmieniu: „klik = przejście"). Skutek: do szczegółów elementu BEZ zobowiązań nie prowadziło
 * z drzewka nic, a każde zajrzenie przestawiało korzeń. Wiersz przestał być `<a>` świadomie —
 * na linku każdy modyfikator ma już właściciela w przeglądarce (Ctrl = nowa karta, Shift = nowe
 * okno, Alt = pobierz), więc gest wejścia nie miałby gdzie zamieszkać. Przycisk „Wejdź" w panelu
 * istniał jeden dzień — user 2026-08-26: dwuklik wystarcza, przycisk do usunięcia (decyzja 160).
 *
 * ⛔ Żadnego menu kontekstowego ani kebaba w wierszu (słowo usera: „opcje na karcie szczegółów").
 *
 * ⭐⭐ Wydajność (2026-08-26, „zamraża się całe drzewko przy szybkich strzałkach"): przy 1700
 * wierszach jedno naciśnięcie strzałki kosztowało ~520 ms, bo ognisko było STANEM Reacta i każdy
 * klawisz przerysowywał wszystkie wiersze. Dziś: ognisko w refie i w DOM (zero renderów), wiersz
 * zapamiętany (`memo`) z handlerami o stałej tożsamości, a rodzic oddaje te same obiekty wierszy,
 * gdy się nie zmieniły (`reuseRows`). Zmierzone przed/po w `progress.md` „2026-08-26 cz. 2".
 */

// Decyzja 58 („relacje = kształt, stany = kolor") mieszka teraz w `shared/ui/RelationIcon`:
// cztery kształty jako WŁASNE SVG, bo są językiem produktu (spec 12B.7).
// ⚠ Do 2026-08-19 były tu znaki czcionki (`■ □ ↓ ↑`) — czyli kształt, o którym decydowała
// czcionka systemowa odbiorcy, a przy nadpisanej czcionce znikał w pusty prostokąt.

/**
 * Jak wiersz ma się do zaznaczenia.
 *
 * ⭐ Zaznaczony jest BYT, nie wiersz (decyzja 157): ten sam element stoi w drzewku bywa
 * kilka razy — jako dziecko po `has` i jako adnotacja zależności pod sąsiadem. Adres niesie
 * identyfikator, więc wszystkie jego wiersze mówią „to ten" — ale tylko jeden, **kliknięty**
 * (po odświeżeniu: pierwszy), dostaje pełny kolor i `aria-selected`; reszta jest pojaśniona
 * („tu też"). Klucz wiersza nie nadaje się do adresu, bo zależy od rozwinięć, których adres
 * nie zna.
 */
export type RowSelection = 'primary' | 'echo' | null;

interface ConstellationTreeProps {
  rows: readonly TreeRow[];
  expanded: ReadonlySet<number>;
  work: ReadonlyMap<number, ElementWorkCounts> | undefined;
  rollup: ReadonlyMap<number, ElementWorkRollup> | undefined;
  /**
   * Zasięg agregatu `Σ` — przełącznik nad drzewkiem (decyzja 171, adres `?sums=near`).
   *
   * ⚠ Wiersz nie liczy z niego niczego; potrzebuje go WYŁĄCZNIE do podpowiedzi. Zasięg
   * zmienia **znaczenie** liczby, nie jej dokładność, więc liczba bez nazwanego zasięgu
   * jest dwuznaczna — a dwuznaczna suma to dokładnie to zgłoszenie, od którego zaczął się
   * ten wątek („sumy dziwnie się sumują", 2026-09-04).
   */
  sumsScope: SumsScope;
  owners: ReadonlyMap<number, OwnerView[]> | undefined;
  /** Zaznaczony element — identyfikator, bo ten sam byt bywa w kilku wierszach. */
  selectedElementId: number | null;
  /** Wiersz zaznaczenia w pełnym kolorze (kliknięty; po odświeżeniu z adresu — pierwszy). */
  primaryRowKey: string | null;
  onToggle: (elementId: number) => void;
  onShowAll: (anchorId: number, section: RelationDirection) => void;
  /** Klik w wiersz albo Enter: zaznaczenie + szczegóły w panelu. Drzewko stoi. */
  onSelect: (row: ElementRow) => void;
  /** Dwuklik albo Shift+Enter: wejście — element staje się nowym korzeniem. */
  onEnter: (row: ElementRow) => void;
  /**
   * Klik w trójkę liczb: to samo zaznaczenie, tylko panel przewinięty do zobowiązań
   * (decyzja 159). Liczby zostają skrótem do roboty, ale nie tracą kontekstu elementu —
   * osobny panel „tylko zobowiązania" dawałby dwie reguły kliknięcia w jednym slocie.
   */
  onOpenCommitments: (row: ElementRow) => void;
  /** Escape: zaznaczenie znika, panel pustoszeje, ognisko zostaje na wierszu. */
  onClearSelection: () => void;
}

const TABBABLE_ROW = '[role="treeitem"][tabindex="0"]';

export function ConstellationTree({
  rows,
  expanded,
  work,
  rollup,
  sumsScope,
  owners,
  selectedElementId,
  primaryRowKey,
  onToggle,
  onShowAll,
  onSelect,
  onEnter,
  onOpenCommitments,
  onClearSelection,
}: ConstellationTreeProps) {
  const { t } = useTranslation();
  const treeRef = useRef<HTMLDivElement>(null);

  /*
   * Roving tabindex: dokładnie JEDEN wiersz jest w kolejności tabulacji. Drzewko
   * z siedemdziesięcioma wierszami, w którym każdy łapie tabulator, jest dla klawiatury
   * pułapką, a nie nawigacją.
   *
   * ⭐ Ognisko jest REFEM, nie stanem Reacta: zmiana ogniska nie zmienia niczego poza atrybutem
   * `tabindex` dwóch wierszy, więc robimy ją wprost w DOM (`onRowFocus`). Render czyta ref, żeby
   * przy następnym przerysowaniu (rozwinięcie, zaznaczenie) zgadzać się z tym, co już jest w DOM.
   */
  const focusedKeyRef = useRef<string | null>(null);

  const elementRows = rows.filter((row): row is ElementRow => row.kind === 'element');
  const keys = new Set(elementRows.map((row) => row.key));
  const focusedKey = focusedKeyRef.current;
  const activeKey =
    focusedKey !== null && keys.has(focusedKey)
      ? focusedKey
      : primaryRowKey !== null && keys.has(primaryRowKey)
        ? primaryRowKey
        : (elementRows[0]?.key ?? null);

  const rowElement = useCallback(
    (key: string) =>
      treeRef.current?.querySelector<HTMLElement>(`[data-row-key="${CSS.escape(key)}"]`) ?? null,
    [],
  );

  /**
   * Ognisko trafiło na wiersz — z klawiatury albo z myszy (słowo usera 2026-08-26: jedno
   * ognisko dla obu, żeby strzałka szła dalej od klikniętego wiersza, nie od poprzedniego).
   * Przenosimy `tabindex` bez renderu.
   */
  const onRowFocus = useCallback(
    (key: string) => {
      if (focusedKeyRef.current === key) {
        return;
      }

      focusedKeyRef.current = key;

      const tree = treeRef.current;
      if (!tree) {
        return;
      }

      for (const previous of tree.querySelectorAll<HTMLElement>(TABBABLE_ROW)) {
        if (previous.dataset['rowKey'] !== key) {
          previous.tabIndex = -1;
        }
      }

      const current = rowElement(key);
      if (current) {
        current.tabIndex = 0;
      }
    },
    [rowElement],
  );

  const focusRow = useCallback((key: string) => rowElement(key)?.focus(), [rowElement]);

  useEffect(() => {
    /*
     * Po wejściu w korzeń strzałki mają działać OD RAZU (słowo usera 2026-08-26) — ognisko ląduje
     * na wierszu w kolejności tabulacji (zaznaczonym albo pierwszym), chyba że coś innego już je
     * trzyma (omnibox, pole formularza). Programowo, nie `autoFocus`: reguła a11y zakazuje atrybutu
     * przy wejściu na stronę, a tu ognisko idzie w główną treść ekranu, po którą człowiek przyszedł.
     */
    const active = document.activeElement;
    if (active !== null && active !== document.body && !treeRef.current?.contains(active)) {
      return;
    }

    treeRef.current?.querySelector<HTMLElement>(TABBABLE_ROW)?.focus();
  }, []);

  /**
   * Klawiatura wiersza (decyzja 158, wariant K2 z makiety): strzałki przenoszą wyłącznie
   * ognisko — panel stoi na ostatnim zaznaczeniu, dopóki człowiek nie naciśnie Enter.
   * Wariant K1 (strzałki zaznaczają, panel podąża) odrzucony słowem usera: podgląd otwiera
   * się na żądanie. Handler o stałej tożsamości, bo dostaje go zapamiętany wiersz.
   */
  const onRowKeyDown = useStableHandler((event: KeyboardEvent<HTMLDivElement>, row: ElementRow) => {
    const index = elementRows.findIndex((candidate) => candidate.key === row.key);
    if (index < 0) {
      return;
    }

    const isExpanded = expanded.has(row.elementId) || row.depth === 0;

    const moveTo = (target: number) => {
      const destination = elementRows[target];
      if (destination) {
        event.preventDefault();
        focusRow(destination.key);
      }
    };

    switch (event.key) {
      case 'ArrowDown':
        moveTo(index + 1);
        break;

      case 'ArrowUp':
        moveTo(index - 1);
        break;

      case 'ArrowRight':
        // Wzorzec WAI-ARIA: pierwsze naciśnięcie rozwija, drugie wchodzi do środka.
        if (isExpandable(row) && !isExpanded) {
          event.preventDefault();
          onToggle(row.elementId);
        } else if (isExpanded) {
          moveTo(index + 1);
        }
        break;

      case 'ArrowLeft':
        if (isExpanded && row.depth > 0) {
          event.preventDefault();
          onToggle(row.elementId);
        } else {
          // Wyżej i płycej — czyli do rodzica, bez zakładania, że stoi tuż nad.
          const parent = elementRows
            .slice(0, index)
            .reverse()
            .find((candidate) => candidate.depth < row.depth);

          if (parent) {
            event.preventDefault();
            focusRow(parent.key);
          }
        }
        break;

      case 'Home':
        moveTo(0);
        break;

      case 'End':
        moveTo(elementRows.length - 1);
        break;

      case 'Enter':
        // Enter = klik, Shift+Enter = dwuklik. Lustro gestów myszy, bo przycisk liczb
        // i trójkąt stoją POZA kolejnością tabulacji (roving tabindex dopuszcza jeden
        // przystanek na wiersz) — bez tego panel byłby dostępny wyłącznie myszą.
        event.preventDefault();
        if (event.shiftKey) {
          onEnter(row);
        } else {
          onSelect(row);
        }
        break;

      case 'Escape':
        // Tylko gdy jest co czyścić: drugi Escape nie ma wyrzucać z drzewka ani zamykać
        // niczego wyżej — więc nie zatrzymujemy go, gdy zaznaczenia nie ma.
        if (selectedElementId !== null) {
          event.preventDefault();
          onClearSelection();
        }
        break;

      default:
        break;
    }
  });

  if (rows.length === 0) {
    return <p className={styles['empty']}>{t('graph.constellation.empty')}</p>;
  }

  return (
    <div
      ref={treeRef}
      className={styles['tree']}
      role="tree"
      aria-label={t('graph.constellation.treeLabel')}
      // `-1`, nie `0`: w kolejność tabulacji wchodzi WIERSZ (roving tabindex),
      // a pojemnik ma być tylko celem programowego ustawienia ogniska.
      tabIndex={-1}
    >
      {rows.map((row) =>
        row.kind === 'more' ? (
          <div key={row.key} className={styles['more']} style={indent(row.depth)}>
            {guides(row.depth)}
            <button
              type="button"
              className={styles['moreButton']}
              onClick={() => onShowAll(row.anchorId, row.section)}
            >
              {/* Wielokropek był glifem `⋯` w treści tłumaczenia — od 2026-08-06 jest
                  ikoną, bo to znak, nie słowo. */}
              <Ellipsis size={15} aria-hidden="true" />
              {t('graph.constellation.showAll', { count: row.hiddenCount })}
            </button>
          </div>
        ) : (
          <Row
            key={row.key}
            row={row}
            selection={
              row.elementId !== selectedElementId
                ? null
                : row.key === primaryRowKey
                  ? 'primary'
                  : 'echo'
            }
            expanded={expanded.has(row.elementId) || row.depth === 0}
            tabbable={row.key === activeKey}
            work={work?.get(row.elementId)}
            rollup={rollup?.get(row.elementId)}
            sumsScope={sumsScope}
            owners={owners?.get(row.elementId)}
            onToggle={onToggle}
            onSelect={onSelect}
            onEnter={onEnter}
            onOpenCommitments={onOpenCommitments}
            onKeyDown={onRowKeyDown}
            onFocus={onRowFocus}
          />
        ),
      )}
    </div>
  );
}

function indent(depth: number) {
  return depth === 0 ? undefined : { paddingLeft: 0 };
}

function guides(depth: number) {
  // Prowadnice są dekoracją — czytnik ekranu dostaje poziom z `aria-level`.
  return Array.from({ length: depth }, (_, i) => (
    <span key={i} className={styles['guide']} aria-hidden="true" />
  ));
}

/**
 * Drugie wciśnięcie dwukliku zaznaczyłoby słowo w tytule — a wejście nie jest edycją
 * tekstu. ⚠ Tylko drugie: przeciągnięcie po tytule dalej kopiuje jego treść.
 */
function swallowDoubleClickSelection(event: MouseEvent<HTMLElement>) {
  if (event.detail > 1) {
    event.preventDefault();
  }
}

/** Kliknięcie w kontrolkę wiersza nie może przy okazji zaznaczyć ani wejść. */
function stopBubbling(event: MouseEvent<HTMLElement>) {
  event.stopPropagation();
}

function selectionClass(selection: RowSelection): string {
  switch (selection) {
    case 'primary':
      return styles['rowSelected'] ?? '';
    case 'echo':
      return styles['rowEcho'] ?? '';
    default:
      return '';
  }
}

interface RowProps {
  row: ElementRow;
  selection: RowSelection;
  expanded: boolean;
  tabbable: boolean;
  work: ElementWorkCounts | undefined;
  rollup: ElementWorkRollup | undefined;
  sumsScope: SumsScope;
  owners: OwnerView[] | undefined;
  onToggle: (elementId: number) => void;
  onSelect: (row: ElementRow) => void;
  onEnter: (row: ElementRow) => void;
  onOpenCommitments: (row: ElementRow) => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>, row: ElementRow) => void;
  onFocus: (key: string) => void;
}

/**
 * Jeden wiersz — ZAPAMIĘTANY. Przerysowuje się tylko wtedy, gdy zmieni się coś jego:
 * własny obiekt `row` (rodzic oddaje ten sam, gdy pola się nie zmieniły), zaznaczenie,
 * rozwinięcie, liczby albo tabindex. Warunek działania: każdy handler ma stałą tożsamość
 * (`useStableHandler` u rodzica) — jedna nowa funkcja w propsach i memo przestaje działać.
 */
const Row = memo(function Row({
  row,
  selection,
  expanded,
  tabbable,
  work,
  rollup,
  sumsScope,
  owners,
  onToggle,
  onSelect,
  onEnter,
  onOpenCommitments,
  onKeyDown,
  onFocus,
}: RowProps) {
  const { t } = useTranslation();
  const expandable = isExpandable(row);

  /*
   * ⭐ Podpowiedź przy `Σ` niesie ZASIĘG, bo bez niego ta sama liczba znaczy dwie różne rzeczy
   *   („cała gałąź" kontra „ten element i dwa poziomy niżej"). ⚠ W trybie wąskim sumy rodzica
   *   i dziecka NIE SKŁADAJĄ SIĘ wzrokiem — `Σ` dziecka sięga o poziom głębiej niż jego wkład
   *   do rodzica — więc nazwanie zasięgu jest tu jedyną obroną przed powtórką zgłoszenia
   *   „sumy dziwnie się sumują".
   * ⛔ Liczba poziomów wchodzi PLACEHOLDEREM ze stałej, na której stoi zapytanie (decyzja 96);
   *   wpisana w tłumaczenie rozjechałaby się z zachowaniem bez żadnego objawu.
   */
  /*
   * „Nic tu nie wisi" — trójka policzona i pusta. ⚠ To NIE jest to samo co `work === undefined`
   * („nie policzono", znak `?`, decyzja 167): tamto dalej dostaje obrys, bo jest stanem, o który
   * warto zapytać ponownie.
   */
  const emptyWork =
    work !== undefined && work.activeCount + work.sleepingCount + work.draugrCount === 0;

  const scopeArgs = {
    scope: t('graph.constellation.sumsScopeName', {
      context: sumsScope,
      levels: NEAR_SUMS_DEPTH,
    }),
  };

  return (
    <div
      className={`${styles['row']} ${selectionClass(selection)}`}
      role="treeitem"
      aria-level={row.depth + 1}
      // `aria-selected` idzie za ZAZNACZENIEM, nie za korzeniem — do 2026-08-26 korzeń
      // udawał zaznaczenie, choć odpowiadał na inne pytanie („gdzie jestem", nie „o czym
      // mówi panel"). Korzeń nie ma dziś własnego wyróżnienia: to zwykły pierwszy wiersz
      // (słowo usera 2026-08-26).
      aria-selected={selection === 'primary'}
      {...(expandable ? { 'aria-expanded': expanded } : {})}
      {...(selection ? { 'data-selection': selection } : {})}
      data-row-key={row.key}
      tabIndex={tabbable ? 0 : -1}
      onFocus={() => onFocus(row.key)}
      onKeyDown={(event) => onKeyDown(event, row)}
      onClick={() => onSelect(row)}
      onDoubleClick={() => onEnter(row)}
      onMouseDown={swallowDoubleClickSelection}
    >
      {guides(row.depth)}

      {expandable ? (
        <button
          type="button"
          className={styles['expander']}
          // Trójkąt jest częścią wiersza, który już ogłasza `aria-expanded` —
          // własna etykieta powtarzałaby stan drugi raz, innymi słowami.
          aria-hidden="true"
          tabIndex={-1}
          onClick={(event) => {
            stopBubbling(event);
            onToggle(row.elementId);
          }}
          onDoubleClick={stopBubbling}
        >
          {expanded ? (
            <ChevronDown size={14} aria-hidden="true" />
          ) : (
            <ChevronRight size={14} aria-hidden="true" />
          )}
        </button>
      ) : (
        <span className={styles['expanderEmpty']} aria-hidden="true" />
      )}

      <span className={styles['mark']} aria-hidden="true">
        <RelationIcon direction={row.direction} size={13} />
      </span>

      {/* Nazwa jest TEKSTEM, nie linkiem (decyzja 157) — zaznaczanie robi cały wiersz,
          a wejście ma własny gest. Atrybut `title` niesie pełną treść, gdy wielokropek
          utnie ją na ekranie (decyzja 65). */}
      <span className={styles['name']} title={row.title}>
        {row.title}
      </span>

      {/* Typ relacji dla czytnika ekranu — na ekranie niesie go kształt. */}
      <span className="visually-hidden">{t(`graph.relation.${row.direction}`)}</span>

      {row.type ? <span className={styles['type']}>{row.type}</span> : null}

      {/* ⚠ Trzy stany, nie dwa (patrz `Unknown`): nie wiemy · nikt · ktoś. Pusty slot przy
          nieznanej odpowiedzi czytałby się jako „element bez właściciela" — a to jest stan
          o skutkach (decyzja 108: przy takim elemencie edycja należy tylko do architekta
          i admina), więc zmyślenie go jest kosztowne. */}
      {owners === undefined ? (
        <Unknown label={t('graph.constellation.unknown')} />
      ) : owners.length > 0 ? (
        <span className={styles['owner']} title={ownerTitle(owners, (source) => t('graph.owner.inheritedFrom', { source }))}>
          {/* ⚠ W wierszu drzewka NIE dopisujemy zdania — wiersz jest gęsty i musi dać się
              przebiec wzrokiem. Nośnikiem tekstowym jest tu `title`, składany niżej. */}
          <span className={accountStateClass(owners[0]!.ownerState, styles, 'ownerOff')}>
            {owners[0]!.displayName}
          </span>
          {owners.length > 1 ? ` +${owners.length - 1}` : ''}
        </span>
      ) : null}

      {row.repeated ? (
        <span className={styles['cycle']} title={t('graph.constellation.cycleHint')}>
          <RotateCcw size={12} aria-hidden="true" />
        </span>
      ) : null}

      <span className={styles['spacer']} />

      {/*
        ROBOTA na tym elemencie — jedyna rodzina liczb, jaka została w wierszu.
        (Decyzja 46 dzieliła je na dwie: robotę po `has` i położenie w sieci po `depends-on`;
        drugą zdjęła decyzja 166. Po `has` liczymy skład i odpowiedzialność — to zostaje.)

        Trójka liczb jest przyciskiem: klik zaznacza TEN element i przewija panel do jego
        zobowiązań, nie ruszając drzewka (decyzja 159). ⚠ `tabIndex={-1}`, bo wiersz ma roving
        tabindex i dopuszcza jeden przystanek tabulatora — z klawiatury tę samą sekcję
        pokazuje Enter na wierszu (bez przewinięcia).
      */}
      <button
        type="button"
        /*
         * ⛔ Pigułka należy się LICZBIE, nie myślnikowi (słowo usera 2026-09-06). Obrys jest
         *   zapowiedzią kliknięcia, a kliknięcie w „nic tu nie wisi" prowadzi do pustej sekcji —
         *   przy drzewku, w którym liście są większością, dawało to ścianę obwódek wokół niczego.
         * ⭐ Krawędź robi się PRZEZROCZYSTA, nie znika: gdyby znikała, wiersz zmieniałby
         *   szerokość w chwili, gdy dochodzą liczby, i cała kolumna drgałaby przy każdym
         *   odświeżeniu. Miejsce zostaje zajęte, widoczność nie.
         * ⚠ Pod wskaźnikiem obrys wraca — akcja dalej istnieje (można tam dopisać zobowiązanie),
         *   więc ma dać się znaleźć; przestaje tylko krzyczeć z każdego pustego wiersza.
         */
        className={`${styles['work']} ${emptyWork ? styles['workEmpty'] : ''}`}
        tabIndex={-1}
        aria-label={t('graph.constellation.openCommitments', { title: row.title })}
        onClick={(event) => {
          stopBubbling(event);
          onOpenCommitments(row);
        }}
        onDoubleClick={stopBubbling}
      >
        {work === undefined ? (
          <Unknown label={t('graph.constellation.unknown')} />
        ) : work.activeCount + work.sleepingCount + work.draugrCount > 0 ? (
          <>
            {work.activeCount > 0 ? (
              <span className={styles['todo']} title={t('graph.constellation.todo')}>
                {work.activeCount}
              </span>
            ) : null}
            {work.sleepingCount > 0 ? (
              <span className={styles['sleeping']} title={t('graph.constellation.sleeping')}>
                <Hourglass size={11} aria-hidden="true" /> {work.sleepingCount}
              </span>
            ) : null}
            {work.draugrCount > 0 ? (
              <span className={styles['draugr']} title={t('graph.constellation.draugr')}>
                <TriangleAlert size={11} aria-hidden="true" /> {work.draugrCount}
              </span>
            ) : null}
          </>
        ) : (
          <span className={styles['quiet']} aria-hidden="true">
            —
          </span>
        )}
      </button>

      {/* Rodzina 1b: AGREGAT `Σ` — ta sama robota, ale zsumowana po poddrzewie `has`
          (decyzja 75) w zasięgu wybranym nad drzewkiem (decyzja 171). Odpowiada na pytanie,
          na które trójka lokalna odpowiedzieć nie może: ile roboty kryje się POD spodem.
          Bez tego zwinięty moduł z czterdziestoma zadaniami w środku wygląda jak pusty.

          ⭐⭐ `Σ` dostaje KAŻDY wiersz, także liść (decyzja 171, uchyla podpunkt decyzji 168).
          Do 2026-09-06 liść miał tu PUSTY SLOT, żeby nie powtarzać liczb obok — ale to
          kosztowało dwie rzeczy naraz: rachunek `Σ(rodzic) = trójka + Σ(dzieci)` nie dawał
          się złożyć wzrokiem (znikał wkład liści, a robota wisi właśnie na nich — zmierzone
          8,9 % na pierwszym poziomie), i slot był CZWARTYM stanem obok liczby, `—` i `?`.
          Dziś stany są trzy i każdy jest prawdą: `Σ n` (policzone), `—` (policzone i pusto),
          `?` (nie policzone). ⚠ Cena przyjęta świadomie: na liściu `Σ` powtarza trójkę obok. */}
      <span className={styles['rollup']}>
        {rollup === undefined ? (
          <Unknown label={t('graph.constellation.unknown')} />
        ) : rollupTotal(rollup) > 0 ? (
          <>
            {/* Tekst składany w JS, a nie rozbity po JSX: „Σ", liczba i znak niepełności
                to jedna informacja, więc mają być jednym węzłem tekstowym — inaczej
                rozjeżdżają się przy kopiowaniu i przy odczycie przez czytnik ekranu.
                ⛔ Zero „do zrobienia" POMIJAMY, tak jak w trójce lokalnej — `Σ` jest tu
                ETYKIETĄ RODZINY, nie przedrostkiem liczby. Do 2026-09-05 było odwrotnie
                w każdej z dwóch rodzin osobno: trójka pomijała zero, a `Σ` je drukowała,
                więc na ekranie stało `Σ 0 ⧗ 2` — „suma zero" obok dwójki czyta się jak
                zepsuta arytmetyka i dokładała się do zgłoszenia o dziwnych sumach. */}
            <span className={styles['rollupSum']} title={t('graph.constellation.rollup', scopeArgs)}>
              {rollup.activeCount > 0
                ? `Σ ${rollup.activeCount}${rollup.partial ? '+' : ''}`
                : `Σ${rollup.partial ? '+' : ''}`}
            </span>
            {rollup.sleepingCount > 0 ? (
              <span
                className={styles['sleeping']}
                title={t('graph.constellation.rollupSleeping', scopeArgs)}
              >
                <Hourglass size={11} aria-hidden="true" /> {rollup.sleepingCount}
              </span>
            ) : null}
            {rollup.draugrCount > 0 ? (
              <span
                className={styles['draugr']}
                title={t('graph.constellation.rollupDraugr', scopeArgs)}
              >
                <TriangleAlert size={11} aria-hidden="true" /> {rollup.draugrCount}
              </span>
            ) : null}
          </>
        ) : (
          <span className={styles['quiet']} aria-hidden="true">
            —
          </span>
        )}
      </span>

      {/* ⛔ Rodziny 2 („położenie w sieci", `↓N`/`↑N`) tu NIE MA — decyzja 166 zamknęła próbę
          wpisaną w decyzję 53. Wiersz niesie już robotę lokalną i agregat `Σ`; trzecia grupa
          cyfr w tym samym miejscu czytała się jako jedna skala, a nie jako inna miara.
          ⚠ Liczby dalej PRZYCHODZĄ z API i są potrzebne — z nich liczy się `expandableCount`
          (czy wiersz dostaje trójkąt) i decyzja, czy dociągać krawędzie. Zniknął ODCZYT
          dla człowieka, nie dana. */}
    </div>
  );
});

/** Czy agregat ma cokolwiek do powiedzenia — trzy liczby razem, nie tylko „do zrobienia". */
function rollupTotal(rollup: ElementWorkRollup): number {
  return rollup.activeCount + rollup.sleepingCount + rollup.draugrCount;
}

/**
 * ⛔⛔ „Nie wiemy" to NIE JEST „zero" — i do 2026-09-04 wiersz rysował jedno i drugie jako `—`.
 *
 * ⭐ Objaw był zmierzony w przeglądarce, nie w bramkach: przy głębokości 2 trzy zapytania
 * drzewka padały naraz (sufit identyfikatorów), a KAŻDY wiersz mówił `—` — czyli element
 * z czterema zadaniami wyglądał na spokojny. To nie był brak informacji, tylko informacja
 * **fałszywa**, i to taka, która nigdzie nie zapala się na czerwono.
 *
 * ⚠ Nośnikiem jest KSZTAŁT (`?` zamiast `—`), nie barwa: barwa alarmowa na stu wierszach naraz
 * przestaje być alarmem, a od alarmu jest pasek nad drzewkiem. Zgodnie z decyzją 58 kolor
 * wzmacnia kształt, nie zastępuje go.
 *
 * ⚠ `—` zostaje `aria-hidden` (to ozdoba pustego miejsca), ale „nie wiemy" **jest treścią**,
 * więc idzie do czytnika ekranu tekstem.
 */
function Unknown({ label }: { label: string }) {
  return (
    <span className={styles['unknown']} title={label}>
      <span className="visually-hidden">{label}</span>
      <span aria-hidden="true">?</span>
    </span>
  );
}

function ownerTitle(owners: readonly OwnerView[], t: (key: string, args?: object) => string) {
  return owners
    .map((owner) => {
      const skad = owner.isLocal
        ? owner.displayName
        : `${owner.displayName} — ${t('graph.owner.inheritedFrom', {
            source: owner.sourceElementTitle ?? `#${owner.sourceElementId}`,
          })}`;

      // ⛔ Tu mieszka nośnik TEKSTOWY wyszarzenia z wiersza — sam kolor nie wystarczy (dec. 58).
      const stan = accountStateKey(owner.ownerState);

      return stan ? `${skad} — ${t(stan)}` : skad;
    })
    .join('\n');
}
